import { AditionInitialization } from '@components/atoms/Adition/AditionInitialization';
import { Button } from '@components/atoms/Button/Button';
import { LoadingIndicator } from '@components/atoms/LoadingIndicator/LoadingIndicator';
import { StreamingTeaser } from '@components/molecules/StreamingTeaser/StreamingTeaser';
import { Transition } from '@headlessui/react';
import { adPositions } from '@lib/adition/adPositions';
import { AditionTagWithFallback, type PropsWithClassProps } from '@vgn-medien-holding/vgn-fe-components';
import { Fragment, type PropsWithChildren, useMemo } from 'react';

interface EntityMixGridProps extends PropsWithChildren, PropsWithClassProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  entities: any[];
  fetching: boolean;
  hasMorePages: boolean;
  onLoadMore: () => void;
}

/**
 * Presentation for the movie/show teaser grid: ad slots, "Mehr laden", the empty
 * state and the loading indicator. Shared by `EntityMix` (the `entities` query)
 * and `EntityMixLegacy` (the separate `movies`/`tvshows` queries), which differ
 * only in how they fetch.
 */
export const EntityMixGrid = ({
  entities,
  fetching,
  hasMorePages,
  onLoadMore,
  children,
  classProps,
}: EntityMixGridProps) => {
  const adSlots = useMemo(() => {
    const slots = new Map<number, number>();
    let count = 0;
    entities?.forEach((_, idx) => {
      if (idx > 1 && idx % 8 === 0 && idx !== 16 && count < 3) {
        slots.set(idx, count);
        count++;
      }
    });
    return slots;
  }, [entities]);

  return (
    <div className={classProps?.root}>
      <AditionInitialization tags={adPositions} />
      {entities?.length > 0 ? (
        <section>
          <div className="grid grid-flow-row-dense grid-cols-fill-streaming-default gap-x-4 gap-y-16">
            {entities.map((entity, idx) => (
              <Fragment key={entity.id}>
                {adSlots.has(idx) && (
                  <div className="col-span-full col-start-1 grid place-items-center">
                    {/*FIXME: maybe add CAD as well*/}
                    <AditionTagWithFallback
                      tag={adPositions[8]}
                      tagCount={adSlots.get(idx)}
                      fallback={adPositions[24 + adSlots.get(idx)]}
                      breakpoint={'lg'}
                    />
                  </div>
                )}
                <Transition
                  as="div"
                  enter="transition-opacity duration-500"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="transition-opacity duration-500"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                  show={true}
                >
                  <StreamingTeaser entry={entity} listicleType={entity?.type?.toUpperCase()} showUpperBookmark />
                </Transition>
              </Fragment>
            ))}
          </div>
          <div className="mt-24">
            {hasMorePages && (
              <div className="text-center">
                <Button
                  title="Mehr laden"
                  hasAction
                  onAction={() => {
                    setTimeout(onLoadMore);
                  }}
                  isLoading={fetching}
                  buttonStyle="secondary"
                  classProps={{ root: 'mt-6' }}
                />
              </div>
            )}
          </div>
        </section>
      ) : (
        !fetching && <>{children}</>
      )}
      <LoadingIndicator visible={fetching && entities?.length < 1} />
    </div>
  );
};
